import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ExportBar from "../../Components/Accounting/ExportBar";
import { ajaxPayroll } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const EmployeeDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState("profile"); // profile | payslips | allowances
  const [showAllow, setShowAllow] = useState(false);
  const [showDeduct,setShowDeduct]= useState(false);
  const [saving,   setSaving]   = useState(false);
  const [allowForm, setAllowForm] = useState({ type:null, amount:"", description:"" });
  const [deductForm,setDeductForm]= useState({ type:null, amount:"", description:"" });

  const ALLOW_TYPES = [
    { value:"housing",    label:"Housing Allowance" },
    { value:"transport",  label:"Transport Allowance" },
    { value:"medical",    label:"Medical Allowance" },
    { value:"lunch",      label:"Lunch Allowance" },
    { value:"other",      label:"Other Allowance" },
  ];
  const DEDUCT_TYPES = [
    { value:"loan",       label:"Loan Repayment" },
    { value:"advance",    label:"Salary Advance" },
    { value:"sacco",      label:"SACCO Deduction" },
    { value:"other",      label:"Other Deduction" },
  ];

  useEffect(() => { if (id) { fetchEmployee(); fetchPayslips(); } }, [id]);

  const fetchEmployee = async () => {
    setLoading(true);
    const res = await ajaxPayroll.getEmployee({ id: parseInt(id) });
    if (res.status === "OK") setEmployee(res.details);
    else { toast.error("Employee not found"); navigate("/accounting/payroll"); }
    setLoading(false);
  };

  const fetchPayslips = async () => {
    const res = await ajaxPayroll.getPayslipByEmployee({ employee_id: parseInt(id) });
    if (res.status === "OK") setPayslips(Array.isArray(res.details) ? res.details : res.details?.list || []);
  };

  const handleAddAllowance = async (e) => {
    e.preventDefault();
    if (!allowForm.type || !allowForm.amount) { toast.error("Type and amount required"); return; }
    setSaving(true);
    const res = await ajaxPayroll.addAllowance({
      employee_id: parseInt(id), type: allowForm.type.value,
      amount: parseFloat(allowForm.amount), description: allowForm.description,
    });
    if (res.status === "OK") { toast.success("Allowance added!"); fetchEmployee(); setShowAllow(false); setAllowForm({ type:null, amount:"", description:"" }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const handleAddDeduction = async (e) => {
    e.preventDefault();
    if (!deductForm.type || !deductForm.amount) { toast.error("Type and amount required"); return; }
    setSaving(true);
    const res = await ajaxPayroll.addDeduction({
      employee_id: parseInt(id), type: deductForm.type.value,
      amount: parseFloat(deductForm.amount), description: deductForm.description,
    });
    if (res.status === "OK") { toast.success("Deduction added!"); fetchEmployee(); setShowDeduct(false); setDeductForm({ type:null, amount:"", description:"" }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const handleRemoveAllowance = async (allowanceId) => {
    if (!window.confirm("Remove this allowance?")) return;
    const res = await ajaxPayroll.removeAllowance({ id: allowanceId });
    if (res.status === "OK") { toast.success("Removed"); fetchEmployee(); }
    else toast.error(res.message);
  };

  const handleRemoveDeduction = async (deductionId) => {
    if (!window.confirm("Remove this deduction?")) return;
    const res = await ajaxPayroll.removeDeduction({ id: deductionId });
    if (res.status === "OK") { toast.success("Removed"); fetchEmployee(); }
    else toast.error(res.message);
  };

  if (loading) return (
    <AppContainer title="Employee" previousPageTitle="Payroll">
      <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div>
    </AppContainer>
  );
  if (!employee) return null;

  const allowances   = employee.allowances  || [];
  const deductions   = employee.deductions  || [];
  const totalAllowances = allowances.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const totalDeductions = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const grossEstimate   = (parseFloat(employee.basic_salary) || 0) + totalAllowances;

  return (
    <AppContainer
      title={employee.full_name}
      previousPageTitle="Payroll"
      description="Employee profile showing basic details, allowances, deductions, and payslip history."
      actions={[
        { label:"Add Allowance",  icon:"fa-solid fa-plus-circle",  className:"btn btn-outline-success",    onClick:() => setShowAllow(true) },
        { label:"Add Deduction",  icon:"fa-solid fa-minus-circle", className:"btn btn-outline-warning",    onClick:() => setShowDeduct(true) },
        { label:"All Employees",  icon:"fa-solid fa-arrow-left",   className:"btn btn-light",               onClick:() => navigate("/accounting/payroll") },
      ]}
    >
      <Toaster />

      {/* Header */}
      <div className="card custom-card p-4 mb-3">
        <div className="row align-items-center">
          <div className="col-auto">
            <div style={{ width:"64px",height:"64px",borderRadius:"50%",background:"#4f46e520",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <span style={{ fontSize:"1.6rem",fontWeight:700,color:"#4f46e5" }}>{employee.full_name?.[0]}</span>
            </div>
          </div>
          <div className="col">
            <h5 className="fw-bold mb-1">{employee.full_name}</h5>
            <div className="d-flex gap-2 flex-wrap">
              <code className="tx-12">{employee.employee_number}</code>
              <span className="badge bg-info text-capitalize">{employee.employment_type}</span>
              <span className={`badge bg-${employee.status==="active"?"success":"secondary"}`}>{employee.status}</span>
            </div>
            {employee.position && <div className="tx-13 tx-muted mt-1">{employee.position}</div>}
          </div>
          <div className="col-lg-4 text-end">
            <div className="tx-12 tx-muted">Basic Salary</div>
            <div className="fw-bold tx-20 tx-primary">{functions.formatCurrency(employee.basic_salary)}</div>
            <div className="tx-11 tx-muted">Gross est: {functions.formatCurrency(grossEstimate)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key:"profile",    label:"Profile",    icon:"fa-user" },
          { key:"allowances", label:`Allowances (${allowances.length})`, icon:"fa-plus-circle" },
          { key:"deductions", label:`Deductions (${deductions.length})`, icon:"fa-minus-circle" },
          { key:"payslips",   label:`Payslips (${payslips.length})`,     icon:"fa-file-lines" },
        ].map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${tab===t.key?"active":""}`} onClick={()=>setTab(t.key)}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* PROFILE TAB */}
      {tab === "profile" && (
        <div className="card custom-card p-4">
          <div className="row g-3 tx-13">
            {[
              { label:"Email",          value: employee.email },
              { label:"Phone",          value: employee.phone },
              { label:"National ID",    value: employee.national_id },
              { label:"TIN",            value: employee.tin },
              { label:"NSSF Number",    value: employee.nssf_number },
              { label:"Bank",           value: employee.bank_name },
              { label:"Bank Account",   value: employee.bank_account },
              { label:"Bank Branch",    value: employee.bank_branch },
              { label:"Hire Date",      value: functions.formatDate(employee.hire_date) },
              { label:"Employment Type",value: employee.employment_type },
              { label:"Basic Salary",   value: functions.formatCurrency(employee.basic_salary) },
              { label:"Status",         value: employee.status },
            ].map((f, i) => (
              <div key={i} className="col-lg-4 col-sm-6">
                <div className="tx-11 tx-muted">{f.label}</div>
                <div className="fw-semibold">{f.value || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ALLOWANCES TAB */}
      {tab === "allowances" && (
        <div className="card custom-card p-3">
          <div className="d-flex justify-content-between mb-3">
            <div>
              <h6 className="fw-semibold mb-0">Allowances</h6>
              <span className="tx-12 tx-muted">Total: <strong className="tx-success">{functions.formatCurrency(totalAllowances)}</strong></span>
            </div>
            <button className="btn btn-sm btn-outline-success" onClick={() => setShowAllow(true)}>
              <i className="fa-solid fa-plus me-1"></i>Add Allowance
            </button>
          </div>
          {allowances.length === 0 ? (
            <p className="tx-muted tx-13 text-center py-3">No allowances configured.</p>
          ) : (
            <table className="table table-sm table-bordered">
              <thead className="bg-light"><tr><th>Type</th><th>Description</th><th className="text-end">Amount</th><th></th></tr></thead>
              <tbody>
                {allowances.map((a, i) => (
                  <tr key={i}>
                    <td className="text-capitalize">{a.type?.replace("_"," ")}</td>
                    <td className="tx-12">{a.description || "—"}</td>
                    <td className="text-end tx-success fw-bold">{functions.formatCurrency(a.amount)}</td>
                    <td><button className="btn btn-xs btn-light tx-danger" onClick={() => handleRemoveAllowance(a.id)}><i className="fa-solid fa-trash" style={{fontSize:"0.7rem"}}></i></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="fw-semibold bg-light">
                <tr><td colSpan={2}>Total</td><td className="text-end tx-success">{functions.formatCurrency(totalAllowances)}</td><td></td></tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* DEDUCTIONS TAB */}
      {tab === "deductions" && (
        <div className="card custom-card p-3">
          <div className="d-flex justify-content-between mb-3">
            <div>
              <h6 className="fw-semibold mb-0">Deductions</h6>
              <span className="tx-12 tx-muted">Total: <strong className="tx-danger">{functions.formatCurrency(totalDeductions)}</strong></span>
            </div>
            <button className="btn btn-sm btn-outline-warning" onClick={() => setShowDeduct(true)}>
              <i className="fa-solid fa-plus me-1"></i>Add Deduction
            </button>
          </div>
          {deductions.length === 0 ? (
            <p className="tx-muted tx-13 text-center py-3">No deductions configured.</p>
          ) : (
            <table className="table table-sm table-bordered">
              <thead className="bg-light"><tr><th>Type</th><th>Description</th><th className="text-end">Amount</th><th></th></tr></thead>
              <tbody>
                {deductions.map((d, i) => (
                  <tr key={i}>
                    <td className="text-capitalize">{d.type?.replace("_"," ")}</td>
                    <td className="tx-12">{d.description || "—"}</td>
                    <td className="text-end tx-danger fw-bold">{functions.formatCurrency(d.amount)}</td>
                    <td><button className="btn btn-xs btn-light tx-danger" onClick={() => handleRemoveDeduction(d.id)}><i className="fa-solid fa-trash" style={{fontSize:"0.7rem"}}></i></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="fw-semibold bg-light">
                <tr><td colSpan={2}>Total</td><td className="text-end tx-danger">{functions.formatCurrency(totalDeductions)}</td><td></td></tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* PAYSLIPS TAB */}
      {tab === "payslips" && (
        <div className="card custom-card p-3">
          <ExportBar data={payslips}
            columns={[{key:"pay_period",label:"Period"},{key:"gross_pay",label:"Gross"},{key:"paye",label:"PAYE"},{key:"net_pay",label:"Net"}]}
            filename={`payslips-${employee.employee_number}`} pdfTitle={`Payslips — ${employee.full_name}`}
            printCode="ACC-PAYROLL" exportCode="ACC-PAYROLL" />
          {payslips.length === 0 ? (
            <p className="tx-muted tx-13 text-center py-3">No payslips yet.</p>
          ) : (
            <table className="table table-sm table-bordered mt-2">
              <thead className="bg-light"><tr><th>Period</th><th>Basic</th><th>Allowances</th><th>Gross</th><th>PAYE</th><th>NSSF</th><th>LST</th><th>Deductions</th><th>Net Pay</th></tr></thead>
              <tbody>
                {payslips.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.pay_period}</strong></td>
                    <td>{functions.formatCurrency(p.basic_salary)}</td>
                    <td className="tx-success">{functions.formatCurrency(p.total_allowances)}</td>
                    <td>{functions.formatCurrency(p.gross_pay)}</td>
                    <td className="tx-danger">{functions.formatCurrency(p.paye)}</td>
                    <td className="tx-danger">{functions.formatCurrency(p.nssf_employee)}</td>
                    <td className="tx-danger">{functions.formatCurrency(p.lst)}</td>
                    <td className="tx-danger">{functions.formatCurrency(p.total_other_deductions)}</td>
                    <td className="tx-success fw-bold">{functions.formatCurrency(p.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ADD ALLOWANCE MODAL */}
      {showAllow && (
        <AppModal
          title="Add Allowance"
          onClose={()=>setShowAllow(false)}
          onSubmit={handleAddAllowance}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={()=>setShowAllow(false)}>Cancel</button><button type="submit" className="btn btn-success" disabled={saving}>{saving?"Adding...":"Add Allowance"}</button>
            </>
          }
        >
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Type</label><Select options={ALLOW_TYPES} value={allowForm.type} onChange={(v)=>setAllowForm({...allowForm,type:v})} /></div>
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Amount (UGX)</label><input type="number" className="form-control" value={allowForm.amount} onChange={(e)=>setAllowForm({...allowForm,amount:e.target.value})} required /></div>
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Description</label><input className="form-control" value={allowForm.description} onChange={(e)=>setAllowForm({...allowForm,description:e.target.value})} /></div>
        </AppModal>
      )}

      {/* ADD DEDUCTION MODAL */}
      {showDeduct && (
        <AppModal
          title="Add Deduction"
          onClose={()=>setShowDeduct(false)}
          onSubmit={handleAddDeduction}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={()=>setShowDeduct(false)}>Cancel</button><button type="submit" className="btn btn-warning" disabled={saving}>{saving?"Adding...":"Add Deduction"}</button>
            </>
          }
        >
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Type</label><Select options={DEDUCT_TYPES} value={deductForm.type} onChange={(v)=>setDeductForm({...deductForm,type:v})} /></div>
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Amount (UGX)</label><input type="number" className="form-control" value={deductForm.amount} onChange={(e)=>setDeductForm({...deductForm,amount:e.target.value})} required /></div>
          <div className="mb-3"><label className="form-label tx-12 fw-semibold">Description</label><input className="form-control" value={deductForm.description} onChange={(e)=>setDeductForm({...deductForm,description:e.target.value})} /></div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default EmployeeDetail;
